import Vue from 'vue'
import api, { setApiToken, getApiToken } from '@/api/client'

const SESSION_KEY = 'blog_session'

const state = Vue.observable({
  user: loadCachedUser(),
  authDialogVisible: false,
  authDialogTab: 'login',
  ready: false
})

let initPromise = null

function loadCachedUser() {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function saveSession(user) {
  if (user) {
    localStorage.setItem(SESSION_KEY, JSON.stringify(user))
  } else {
    localStorage.removeItem(SESSION_KEY)
  }
  state.user = user
}

function notifyChange() {
  window.dispatchEvent(new Event('auth-change'))
}

/** 仅凭证失效 / 封禁才清本地登录；网络抖动不掉线 */
function isAuthFatalError(err) {
  if (!err) return false
  const code = Number(err.code)
  const http = Number(err.httpStatus || err.status || 0)
  if (code === 401 || http === 401) return true
  if (code === 403 || http === 403) return true
  const msg = String(err.message || '')
  return /未登录|登录已过期|账号已被封禁|token/i.test(msg) && (http === 401 || http === 403 || code === 401 || code === 403)
}

function clearLocalSession() {
  setApiToken(null)
  saveSession(null)
}

async function fetchAndSetUser() {
  const res = await api.me()
  saveSession(res.data)
  return res.data
}

export async function initAuth() {
  if (initPromise) return initPromise

  initPromise = (async () => {
    if (!getApiToken()) {
      // 无 token 但残留用户缓存时清掉，避免「看起来已登录」
      if (state.user) saveSession(null)
      state.ready = true
      return
    }
    try {
      await fetchAndSetUser()
    } catch (e) {
      if (isAuthFatalError(e)) {
        clearLocalSession()
        notifyChange()
      }
      // 网络/5xx：保留 localStorage 缓存用户，继续视为登录
    } finally {
      state.ready = true
    }
  })()

  try {
    await initPromise
  } finally {
    // 保持 resolved promise，避免路由重复 init 时反复打 /me
  }
  return initPromise
}

function bindSessionKeepAlive() {
  if (typeof window === 'undefined' || bindSessionKeepAlive.done) return
  bindSessionKeepAlive.done = true
  // 浏览器后退从 bfcache 恢复时，软刷新资料，失败不掉线
  window.addEventListener('pageshow', (e) => {
    if (e.persisted && getApiToken()) {
      auth.refreshUser({ soft: true })
    }
  })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && getApiToken()) {
      auth.refreshUser({ soft: true })
    }
  })
}

export const auth = {
  get user() {
    return state.user
  },

  get isLoggedIn() {
    return !!state.user
  },

  get ready() {
    return state.ready
  },

  get points() {
    return (state.user && state.user.points) || 0
  },

  get checkin() {
    return (state.user && state.user.checkin) || null
  },

  get isMember() {
    if (!state.user || !state.user.isMember) return false
    if (!state.user.memberExpireAt) return true
    return new Date(state.user.memberExpireAt) >= new Date()
  },

  get canAccessAdmin() {
    return !!(state.user && state.user.adminAccess && state.user.adminAccess.canAccessAdmin)
  },

  get isSuperAdmin() {
    return !!(state.user && state.user.adminAccess && state.user.adminAccess.isSuperAdmin)
  },

  get adminPermissions() {
    return (state.user && state.user.adminAccess) || {}
  },

  canManageArticleType(type) {
    const perms = this.adminPermissions
    if (!perms.manageArticles) return false
    if (perms.isSuperAdmin) return true
    const types = perms.articleTypes || []
    if (!types.length) return true
    return types.includes(type)
  },

  get canManageUsers() {
    return !!(this.adminPermissions.manageUsers || this.adminPermissions.isSuperAdmin)
  },

  get canManageOrders() {
    return !!(this.adminPermissions.manageOrders || this.adminPermissions.isSuperAdmin)
  },

  get canManageMemberPlans() {
    return !!(this.adminPermissions.manageMemberPlans || this.adminPermissions.isSuperAdmin)
  },

  get canManageComments() {
    return !!(this.adminPermissions.manageComments || this.adminPermissions.isSuperAdmin)
  },

  hasPurchased(articleId) {
    if (!state.user) return false
    const id = Number(articleId)
    const purchased = state.user.purchasedArticles || []
    const granted = state.user.grantedArticles || []
    return purchased.includes(id) || granted.includes(id)
  },

  getSponsorOrders() {
    if (!state.user) return []
    return [...(state.user.sponsorOrders || [])].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    )
  },

  get authDialogVisible() {
    return state.authDialogVisible
  },

  set authDialogVisible(val) {
    state.authDialogVisible = val
  },

  get authDialogTab() {
    return state.authDialogTab
  },

  set authDialogTab(val) {
    state.authDialogTab = val
  },

  openAuthDialog(tab = 'login') {
    state.authDialogTab = tab
    state.authDialogVisible = true
  },

  closeAuthDialog() {
    state.authDialogVisible = false
  },

  async register({ username, email, password, code }) {
    try {
      const res = await api.register({ username, email, password, code })
      setApiToken(res.data.token)
      saveSession(res.data.user)
      state.authDialogVisible = false
      notifyChange()
      return { success: true, message: res.message || '注册成功' }
    } catch (e) {
      return { success: false, message: e.message || '注册失败' }
    }
  },

  async login({ account, password }) {
    try {
      const res = await api.login({ account, password })
      setApiToken(res.data.token)
      saveSession(res.data.user)
      state.authDialogVisible = false
      notifyChange()
      return { success: true, message: res.message || '登录成功' }
    } catch (e) {
      return { success: false, message: e.message || '登录失败' }
    }
  },

  /** 扫码登录成功后写入会话 */
  applyTokenSession({ token, user }) {
    if (!token || !user) return
    setApiToken(token)
    saveSession(user)
    state.authDialogVisible = false
    notifyChange()
  },

  async createQrLogin() {
    try {
      const res = await api.createQrLogin()
      return { success: true, data: res.data }
    } catch (e) {
      return { success: false, message: e.message || '创建二维码失败' }
    }
  },

  async pollQrLogin(ticket) {
    try {
      const res = await api.pollQrLogin(ticket)
      return { success: true, data: res.data, message: res.message }
    } catch (e) {
      return { success: false, message: e.message || '查询失败' }
    }
  },

  async cancelQrLogin(ticket) {
    try {
      await api.cancelQrLogin(ticket)
      return { success: true }
    } catch (e) {
      return { success: false, message: e.message }
    }
  },

  async createWxBind() {
    try {
      const res = await api.createWxBind()
      return { success: true, data: res.data }
    } catch (e) {
      return { success: false, message: e.message || '创建绑定二维码失败' }
    }
  },

  async pollWxBind(ticket) {
    try {
      const res = await api.pollWxBind(ticket)
      if (res.data && res.data.user) {
        saveSession(res.data.user)
        notifyChange()
      }
      return { success: true, data: res.data, message: res.message }
    } catch (e) {
      return { success: false, message: e.message || '查询失败' }
    }
  },

  async cancelWxBind(ticket) {
    try {
      await api.cancelWxBind(ticket)
      return { success: true }
    } catch (e) {
      return { success: false, message: e.message }
    }
  },

  async unbindWx() {
    try {
      const res = await api.unbindWx()
      if (res.data) {
        saveSession(res.data)
        notifyChange()
      }
      return { success: true, message: res.message || '已解绑' }
    } catch (e) {
      return { success: false, message: e.message || '解绑失败' }
    }
  },

  async sendRegisterCode(email) {
    try {
      const res = await api.sendRegisterCode(email)
      return { success: true, message: res.message || '验证码已发送' }
    } catch (e) {
      return { success: false, message: e.message || '发送失败' }
    }
  },

  async sendResetCode(email) {
    try {
      const res = await api.sendResetCode(email)
      return { success: true, message: res.message || '验证码已发送' }
    } catch (e) {
      return { success: false, message: e.message || '发送失败' }
    }
  },

  async resetPassword({ email, code, password }) {
    try {
      const res = await api.resetPassword({ email, code, password })
      if (state.user && state.user.email === email) {
        clearLocalSession()
        notifyChange()
      }
      return { success: true, message: res.message || '密码重置成功' }
    } catch (e) {
      return { success: false, message: e.message || '重置失败' }
    }
  },

  /**
   * @param {{ soft?: boolean }} [opts] soft=true：失败不强制清会话（用于 bfcache / 切回标签）
   */
  async refreshUser(opts = {}) {
    if (!getApiToken()) return
    try {
      await fetchAndSetUser()
      notifyChange()
    } catch (e) {
      if (isAuthFatalError(e)) {
        clearLocalSession()
        notifyChange()
      }
      // soft / 网络错误：保留缓存用户，不掉线
      if (!opts.soft && !isAuthFatalError(e)) {
        // 仍保留会话；调用方无需处理
      }
    }
  },

  async activateMember(planId = 'monthly') {
    // 兼容旧调用：与 createAlipayOrder 相同，返回支付参数而非直接开通
    return this.createAlipayOrder(planId)
  },

  async createAlipayOrder(planId = 'monthly') {
    if (!state.user) return { success: false, message: '请先登录' }
    try {
      let res
      try {
        res = await api.createAlipayOrder(planId)
      } catch (e) {
        // 兼容未部署新路由时回退 /member/activate（已改为下单）
        if (e && (e.httpStatus === 404 || /not found|404/i.test(String(e.message || '')))) {
          res = await api.activateMember(planId)
        } else {
          throw e
        }
      }
      const data = res.data || {}
      if (data.payHtml || data.qrCode || data.orderId) {
        return {
          success: true,
          needsPayment: true,
          message: res.message || '请完成支付宝支付',
          data
        }
      }
      await fetchAndSetUser()
      notifyChange()
      return { success: true, message: res.message || '赞助成功', data }
    } catch (e) {
      return { success: false, message: e.message || '创建支付订单失败' }
    }
  },

  async queryAlipayOrder(orderId) {
    if (!state.user) return { success: false, message: '请先登录' }
    try {
      const res = await api.queryAlipayOrder(orderId)
      if (res.data && res.data.paid) {
        await fetchAndSetUser()
        notifyChange()
      }
      return { success: true, data: res.data }
    } catch (e) {
      return { success: false, message: e.message || '查询失败' }
    }
  },

  async redeemCdk(code) {
    if (!state.user) return { success: false, message: '请先登录' }
    try {
      const res = await api.redeemCdk(code)
      await fetchAndSetUser()
      notifyChange()
      return {
        success: true,
        message: res.message || '兑换成功',
        data: res.data
      }
    } catch (e) {
      return { success: false, message: e.message || '兑换失败' }
    }
  },

  async checkin() {
    if (!state.user) return { success: false, message: '请先登录' }
    const status = (state.user && state.user.checkin) || {}
    if (status.checkedInToday) {
      return { success: true, alreadyCheckedIn: true, message: '今日已签到', data: status }
    }
    try {
      const res = await api.checkin()
      await fetchAndSetUser()
      notifyChange()
      const data = res.data || {}
      return {
        success: true,
        alreadyCheckedIn: !!data.alreadyCheckedIn,
        message: res.message || data.message || '签到成功',
        data
      }
    } catch (e) {
      return { success: false, message: e.message || '签到失败' }
    }
  },

  async getPointsLog(limit = 50) {
    if (!state.user) return []
    try {
      const res = await api.getPointsLog(limit)
      return res.data || []
    } catch {
      return []
    }
  },

  async purchaseArticle(articleId, price) {
    if (!state.user) return { success: false, message: '请先登录' }
    try {
      const res = await api.purchaseArticle(articleId, price)
      await fetchAndSetUser()
      notifyChange()
      return { success: true, message: res.message || '解锁成功' }
    } catch (e) {
      return { success: false, message: e.message || '解锁失败' }
    }
  },

  async logout() {
    try {
      await api.logout()
    } catch {
      // 忽略网络错误，本地仍清除会话
    }
    clearLocalSession()
    notifyChange()
  },

  async updateProfile(data) {
    if (!state.user) return { success: false, message: '未登录' }
    try {
      const res = await api.updateProfile(data)
      saveSession(res.data)
      notifyChange()
      return { success: true, message: res.message || '保存成功' }
    } catch (e) {
      return { success: false, message: e.message || '保存失败' }
    }
  },

  async uploadAvatar(file) {
    if (!state.user) return { success: false, message: '未登录' }
    try {
      const res = await api.uploadAvatar(file)
      saveSession(res.data)
      notifyChange()
      return { success: true, message: res.message || '头像已更新' }
    } catch (e) {
      return { success: false, message: e.message || '上传失败' }
    }
  },

  async changePassword({ currentPassword, newPassword }) {
    if (!state.user) return { success: false, message: '未登录' }
    try {
      const res = await api.changePassword({ currentPassword, newPassword })
      return { success: true, message: res.message || '密码修改成功' }
    } catch (e) {
      return { success: false, message: e.message || '修改失败' }
    }
  }
}

bindSessionKeepAlive()

export default auth
